import React from "react";
import { Truck, FileText, Video, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";

const pricingItems = [
  {
    icon: <Video className="w-6 h-6 text-[#005eb8]" />,
    iconBg: "bg-[#e0f2fe]",
    label: "Initial Consultation",
    oldPrice: "£95",
    newPrice: "£0",
    description: "Thorough health history & condition assessment.",
    highlight: true,
  },
  {
    icon: <RefreshCw className="w-6 h-6 text-[#2ba78c]" />,
    iconBg: "bg-[#e8f5e9]",
    label: "Follow-up",
    oldPrice: "£49",
    newPrice: "£0",
    description: "Monitor progress and address concerns.",
    highlight: true,
  },
  {
    icon: <Truck className="w-6 h-6 text-[#4caf50]" />,
    iconBg: "bg-[#e8f5e9]",
    label: "Prescription Delivery (UK Next-Day)",
    freeLabel: "Free",
    description: "Secure, reliable deliveries of prescribed medications.",
    highlight: false,
  },
  {
    icon: <FileText className="w-6 h-6 text-[#012d5c]" />,
    iconBg: "bg-[#e0f2fe]",
    label: "Prescription Fee",
    flatFee: "£4.99",
    feeSubLabel: "Per prescription",
    feeTag: "Admin Fee",
    description: null,
    highlight: false,
  },
];

const Pricing = () => {
  return (
    <section className="py-28 bg-gradient-to-b from-[#f0f4f8] to-white" id="pricing">
      <div className="container mx-auto px-4">

        {/* Section header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="inline-block bg-[#e8f5e9] text-[#2d8c6e] text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-5">
            No Hidden Fees
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#012d5c] mb-6 leading-tight">
            Accessible Healthcare,<br className="hidden sm:block" /> Transparent Pricing
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Our model prioritises patient care. Clinical consultations are provided at no cost. A £4.99 flat fee per prescription applies if one is issued following your assessment.
          </p>
        </div>

        {/* Pricing cards grid */}
        <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">

          {/* Card: Initial Consultation */}
          <div className="bg-white border-2 border-[#005eb8]/20 rounded-2xl p-8 shadow-md flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-[#e0f2fe] flex items-center justify-center shrink-0">
                <Video className="w-6 h-6 text-[#005eb8]" />
              </div>
              <h3 className="text-lg font-bold text-[#012d5c]">Initial Consultation</h3>
            </div>
            <div className="flex items-end gap-3 mb-4">
              <s className="text-xl text-gray-400 font-medium leading-none mb-1">£95</s>
              <span className="text-5xl font-extrabold text-[#012d5c] leading-none">£0</span>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mt-auto">
              Thorough health history &amp; condition assessment.
            </p>
          </div>

          {/* Card: Follow-up */}
          <div className="bg-white border-2 border-[#2ba78c]/20 rounded-2xl p-8 shadow-md flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-[#e8f5e9] flex items-center justify-center shrink-0">
                <RefreshCw className="w-6 h-6 text-[#2ba78c]" />
              </div>
              <h3 className="text-lg font-bold text-[#012d5c]">Follow-up</h3>
            </div>
            <div className="flex items-end gap-3 mb-4">
              <s className="text-xl text-gray-400 font-medium leading-none mb-1">£49</s>
              <span className="text-5xl font-extrabold text-[#012d5c] leading-none">£0</span>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mt-auto">
              Monitor progress and address concerns.
            </p>
          </div>

          {/* Card: Prescription Delivery */}
          <div className="bg-white border-2 border-[#4caf50]/20 rounded-2xl p-8 shadow-md flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-[#e8f5e9] flex items-center justify-center shrink-0">
                <Truck className="w-6 h-6 text-[#4caf50]" />
              </div>
              <h3 className="text-lg font-bold text-[#012d5c]">Free UK next day deliveries</h3>
            </div>
            <div className="mb-4">
              <span className="text-4xl font-extrabold text-[#4caf50] leading-none">Free</span>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mt-auto">
              Secure, reliable deliveries of prescribed medications.
            </p>
          </div>

          {/* Card: Admin Fee */}
          <div className="bg-white border-2 border-[#012d5c]/20 rounded-2xl p-8 shadow-md flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-[#e0f2fe] flex items-center justify-center shrink-0">
                <FileText className="w-6 h-6 text-[#012d5c]" />
              </div>
              <h3 className="text-lg font-bold text-[#012d5c]">Flat Fee Per Prescription</h3>
            </div>
            <div className="mb-2">
              <span className="text-2xl font-semibold text-[#012d5c] leading-none">£4.99</span>
            </div>
            <p className="text-gray-500 text-sm mb-1">Per prescription</p>
            <span className="inline-block bg-[#e0f2fe] text-[#005eb8] text-xs uppercase tracking-wider px-2.5 py-1 rounded w-fit">
              Flat Fee Per Prescription
            </span>
          </div>

        </div>

        {/* Prescribing note */}
        <div className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-2xl p-6">
          <p className="text-sm text-gray-600 leading-relaxed text-center">
            <strong>Please note:</strong> Prescribing decisions are made by our clinical team following an MDT review. Completing a consultation allows our clinical team to assess your suitability and determine the appropriate next steps.
          </p>
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <Button
            className="bg-[#4caf50] hover:bg-[#3d8b40] text-white px-12 py-6 rounded-xl text-lg font-bold shadow-lg"
            onClick={() =>
              window.location.href =
                "https://app.integroclinics.com/v2/registration"
            }
          >
            Check Your Eligibility
          </Button>
        </div>

      </div>
    </section>
  );
};

export default Pricing;
